import { useState } from 'react';
import {
  FiCheckCircle as CheckCircle2,
  FiChevronDown as ChevronDown,
  FiPlay as Play,
} from 'react-icons/fi';
import { TbCrown as Crown } from 'react-icons/tb';
import { mentoringConfig, ui } from '../constants';
import { usePricingTariffs } from '../context/PricingTariffsContext';
import MentoringVideoModal from './MentoringVideoModal';

interface MentoringCardProps {
  enabled: boolean;
  onToggle: (checked: boolean) => void;
}

function MentoringCard({ enabled, onToggle }: MentoringCardProps) {
  const { mentoringPlans } = usePricingTariffs();
  const plan = mentoringPlans[0];
  const [detailsExpanded, setDetailsExpanded] = useState(false);
  const [videoOpen, setVideoOpen] = useState(false);
  const [videoFailed, setVideoFailed] = useState(false);

  const isPurchaseDisabled = Boolean(mentoringConfig.disabled);
  const disabledDescription =
    mentoringConfig.disabledDescription ||
    'ظرفیت این دوره تکمیل شده است و فعلاً امکان فعال‌سازی افزونه منتورینگ وجود ندارد.';
  const isActive = enabled && !isPurchaseDisabled;

  function openVideo() {
    if (isPurchaseDisabled) return;
    setVideoFailed(false);
    setVideoOpen(true);
  }

  if (!plan) return null;

  const card = (
    <section
      className={`mentoring-card rounded-4 ${isActive ? 'enabled' : 'bg-white dark__bg-card-dark'} ${
        isPurchaseDisabled ? 'purchase-disabled' : ''
      }`}
      aria-disabled={isPurchaseDisabled || undefined}
    >
      <div className="tracker-top">
        <div className="tracker-title">
          <span className="tracker-icon mentoring-icon rounded-3">
            <Crown size={27} />
          </span>
          <div>
            <span className="eyebrow mentoring-eyebrow fs--2">{mentoringConfig.eyebrow}</span>
            <h2 className="text-1000">{mentoringConfig.title}</h2>
            <p className="text-secondary fs--2">{mentoringConfig.cardDescription}</p>
          </div>
        </div>

        <label
          className={`switch-label mentoring-switch fs--2 ${isPurchaseDisabled ? 'disabled' : ''}`}
        >
          <span className="text-secondary">
            {isPurchaseDisabled
              ? 'تکمیل ظرفیت'
              : enabled
                ? ui.common.active
                : ui.common.addToPlan}
          </span>
          <input
            type="checkbox"
            checked={isActive}
            disabled={isPurchaseDisabled}
            onChange={(event) => onToggle(event.target.checked)}
          />
          <span className="switch rounded-pill" aria-hidden="true">
            <span className="rounded-circle" />
          </span>
        </label>
      </div>

      <div className="mentoring-body">
        <button
          type="button"
          className="mentoring-video rounded-3"
          onClick={openVideo}
          disabled={isPurchaseDisabled}
          aria-label={`پخش ${mentoringConfig.video.label}`}
        >
          <img src={mentoringConfig.video.poster} alt={mentoringConfig.video.label} />
          <span className="video-play rounded-circle">
            <Play size={20} fill="currentColor" />
          </span>
          <span className="video-label">{mentoringConfig.video.label}</span>
        </button>

        <div className="mentoring-options">
          <article
            className={`mentoring-plan mentoring-plan-single rounded-3 ${isActive ? 'selected' : ''}`}
          >
            <div className="mentoring-plan-header">
              <span className="mentoring-plan-copy">
                <strong className="text-1000">{plan.name}</strong>
                <small className="text-secondary">{plan.description}</small>
              </span>
              <strong className="mentoring-plan-price fs--2">
                {plan.priceLabel}
              </strong>
            </div>

            <button
              type="button"
              className="mentoring-details-toggle"
              onClick={() => setDetailsExpanded((current) => !current)}
              disabled={isPurchaseDisabled}
              aria-expanded={detailsExpanded}
            >
              {detailsExpanded ? ui.common.closeDetails : ui.common.openDetails}
              <ChevronDown
                size={16}
                className={detailsExpanded ? 'rotated' : ''}
              />
            </button>

            {detailsExpanded ? (
              <ul className="mentoring-features text-secondary fs--2">
                {plan.features.map((feature) => (
                  <li key={feature}>
                    <CheckCircle2 size={15} />
                    {feature}
                  </li>
                ))}
              </ul>
            ) : null}
          </article>
        </div>
      </div>
    </section>
  );

  return (
    <>
      {isPurchaseDisabled ? (
        <div className="tracker-card-wrap tracker-card-wrap--disabled">
          <div className="tracker-card-disabled-tooltip rounded-3 fs--2" role="tooltip">
            {disabledDescription}
          </div>
          {card}
        </div>
      ) : (
        card
      )}

      <MentoringVideoModal
        show={videoOpen}
        onClose={() => setVideoOpen(false)}
        videoFailed={videoFailed}
        onVideoError={() => setVideoFailed(true)}
      />
    </>
  );
}

export default MentoringCard;
